// src/app/modules/collaborateur/MesAvantages.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Gift,
    Heart,
    Bike,
    Home,
    Coffee,
    Baby,
    Plane,
    Settings,
    ChevronDown,
    ChevronUp,
    Info,
    Calendar,
    DollarSign,
    CheckCircle,
    Plus,
    Eye,
} from "lucide-react";
import "../MesrequetesStyles/MesAvantages.css";
import "../MesrequetesStyles/MesOrdresShared.css";

// Action Redux à créer pour récupérer les avantages du collaborateur
import { fetchMesAvantagesRequest } from "../../../../redux/actions/mesAvantagesActions";
import { createAvantageSocialRequestRequest, fetchMyAvantageSocialRequestsRequest } from "../../../../redux/actions/avantageSocialRequestActions";

const DEMO_MODE = false; // 👉 mets false pour repasser en mode normal

const mockCategories = [
    { id: 1, nom: "Restauration", couleur: "#f59e0b", icone: "Coffee" },
    { id: 2, nom: "Santé", couleur: "#ef4444", icone: "Heart" },
    { id: 3, nom: "Transport", couleur: "#3b82f6", icone: "Bike" },
];

const mockAvantagesDisponibles = [
    {
        id: 1, categorieId: 1, nom: "Tickets Restaurant", code: "TICKET_RESTO",
        description: "Prise en charge partielle des repas", montant: 40, frequence: "JOUR", actif: true,
        regle: { nom: "Tous les collaborateurs", condition: "TOUS", valeur: "" },
    },
    {
        id: 2, categorieId: 2, nom: "Mutuelle Santé", code: "MUTUELLE",
        description: "Complémentaire santé familiale", montant: 350, frequence: "MENSUEL", actif: true,
        regle: { nom: "Ancienneté 12 mois", condition: "ANCIENNETE_MOIS_MIN", valeur: "12" },
    },
    {
        id: 3, categorieId: 3, nom: "Indemnité Transport", code: "TRANSPORT_IND",
        description: "Participation aux frais de déplacement", montant: 500, frequence: "MENSUEL", actif: true,
        regle: { nom: "Tous les collaborateurs", condition: "TOUS", valeur: "" },
    },
    {
        id: 4, categorieId: 2, nom: "Abonnement Salle de Sport", code: "SPORT",
        description: "Prise en charge partielle d'un abonnement sportif", montant: 200, frequence: "ANNUEL", actif: true,
        regle: { nom: "Tous les collaborateurs", condition: "TOUS", valeur: "" },
    },
];

const mockAvantagesAttribues = [
    {
        id: 1, nom: "Tickets Restaurant", description: "Prise en charge partielle des repas",
        modeAttribution: "AUTOMATIQUE",
    },
    {
        id: 3, nom: "Indemnité Transport", description: "Participation aux frais de déplacement",
        modeAttribution: "AUTOMATIQUE",
    },
];

const mockAvantages = mockAvantagesDisponibles; // pour le compteur "Total des avantages"

const mockMyRequests = [
    {
        id: 301, avantageSocialId: 2, avantageNom: "Mutuelle Santé", motif: "Ajout d'un enfant à charge",
        status: "PENDING_MANAGER", createdAt: "2026-07-28",
    },
    {
        id: 302, avantageSocialId: 4, avantageNom: "Abonnement Salle de Sport", motif: "Nouvelle inscription",
        status: "APPROVED", createdAt: "2026-06-15",
    },
    {
        id: 303, avantageSocialId: 2, avantageNom: "Mutuelle Santé", motif: "Changement de formule",
        status: "REJECTED", createdAt: "2026-05-10",
    },
];

const MesAvantages = () => {
    const dispatch = useDispatch();

    // État Redux - adapter selon votre structure
    const reduxMesAvantages = useSelector((state) => state.mesAvantages || {});
    const reduxRequests = useSelector((state) => state.avantageSocialRequests || {});

    const loading = DEMO_MODE ? false : reduxMesAvantages.loading;
    const avantages = DEMO_MODE ? mockAvantages : reduxMesAvantages.avantages;
    const avantagesDisponibles = DEMO_MODE ? mockAvantagesDisponibles : reduxMesAvantages.avantagesDisponibles;
    const avantagesAttribues = DEMO_MODE ? mockAvantagesAttribues : reduxMesAvantages.avantagesAttribues;
    const categories = DEMO_MODE ? mockCategories : reduxMesAvantages.categories;
    const myRequests = DEMO_MODE ? mockMyRequests : (reduxRequests.myRequests || []);

    const [expandedCategories, setExpandedCategories] = useState(new Set());
    const [selectedAvantage, setSelectedAvantage] = useState(null);
    const [requestMotif, setRequestMotif] = useState("");
    const [requestComment, setRequestComment] = useState("");
    const [requestError, setRequestError] = useState("");
    const [showBenefitPicker, setShowBenefitPicker] = useState(false);
    const [avantagesTab, setAvantagesTab] = useState("demandes");
    // Conservé à false : l'affichage des avantages attribués est désormais un onglet.
    const [showAssignedBenefits, setShowAssignedBenefits] = useState(false);

    // Charger les avantages au montage
    useEffect(() => {
        if (!DEMO_MODE) {
            dispatch(fetchMesAvantagesRequest());
        }
    }, [dispatch]);

    useEffect(() => {
        if (!DEMO_MODE) {
            dispatch(fetchMyAvantageSocialRequestsRequest());
        }
    }, [dispatch]);

    // Grouper les avantages par catégorie
    const avantagesParCategorie = useMemo(() => {
        if (!avantagesDisponibles || !categories) return [];

        const grouped = categories.map((cat) => {
            const avantageCat = (avantagesDisponibles || []).filter(
                (av) => av.categorieId === cat.id && av.actif
            );
            return {
                ...cat,
                avantages: avantageCat,
                totalMontant: avantageCat.reduce((sum, av) => sum + (av.montant || 0), 0),
            };
        }).filter((cat) => cat.avantages.length > 0);

        return grouped;
    }, [avantagesDisponibles, categories]);

    // Calculer le montant total
    const montantTotal = useMemo(() => {
        return avantagesParCategorie.reduce((sum, cat) => sum + cat.totalMontant, 0);
    }, [avantagesParCategorie]);

    // Toggle expansion d'une catégorie
    const toggleCategory = (catId) => {
        setExpandedCategories((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(catId)) {
                newSet.delete(catId);
            } else {
                newSet.add(catId);
            }
            return newSet;
        });
    };

    // Mapper les icônes
    const getIconComponent = (name) => {
        const map = { Coffee, Heart, Bike, Home, Gift, Baby, Plane, Settings };
        return map[name] || Settings;
    };

    // Mapper les labels de fréquence
    const getFrequenceLabel = (freq) => {
        const map = {
            MENSUEL: "Par mois",
            ANNUEL: "Par an",
            JOUR: "Par jour",
            UNIQUE: "Unique",
        };
        return map[freq] || freq;
    };

    // Ouvrir le détail d'un avantage
    const openDetail = (avantage) => {
        setSelectedAvantage(avantage);
        setShowBenefitPicker(false);
    };

    const closeDetail = () => {
        setSelectedAvantage(null);
        setRequestMotif("");
        setRequestComment("");
        setRequestError("");
    };

    const submitRequest = () => {
        dispatch(createAvantageSocialRequestRequest({
            avantageSocialId: selectedAvantage.id, motif: requestMotif.trim() || null, commentaire: requestComment.trim() || null,
        }));
        closeDetail();
    };

    const statusLabel = (status) => ({
        PENDING_MANAGER: "En attente manager", PENDING_ADMIN: "En attente RH",
        APPROVED: "Approuvée", REJECTED: "Refusée",
    }[status] || status);

    return (
        <div className="maPage mesOrdresStyle mesOrdresBenefits">
            {/* Header */}
            <div className="maHeader">
                <div>
                    <h1>Mes avantages sociaux</h1>
                    <p>Découvrez tous vos avantages et primes</p>
                    {loading && <div className="maMuted" style={{ marginTop: 6 }}>Chargement...</div>}
                </div>
            </div>

            {/* Résumé global */}
            <div className="moTopHeader"><div><h1>Mes avantages sociaux</h1><p>Soumettez une nouvelle demande et suivez son avancement.</p></div><button type="button" className="moPageAction" onClick={() => setShowBenefitPicker(true)}><Plus size={15} /> Nouvelle demande d'avantage</button></div>
            <div className="moRequestCard"><div className="moRequestCard__header"><h2>Avantages sociaux</h2><div className="moTabs"><button type="button" className={avantagesTab === "demandes" ? "is-active" : ""} onClick={() => setAvantagesTab("demandes")}>Mes demandes</button><button type="button" className={avantagesTab === "attribues" ? "is-active" : ""} onClick={() => setAvantagesTab("attribues")}>Mes avantages attribués</button></div></div><div className="moRequestCard__body">{avantagesTab === "demandes" ? (loading && myRequests.length === 0 ? <div className="moEmpty"><Gift size={22} /><span>Chargement...</span></div> : myRequests.length === 0 ? <div className="moEmpty"><Gift size={22} /><strong>Aucune demande enregistrée</strong><span>Cliquez sur « Nouvelle demande d'avantage » pour commencer.</span></div> : <div className="moTableWrap"><table className="moTable"><thead><tr><th>Avantage</th><th>Motif</th><th>Demandée le</th><th>Statut</th><th aria-label="Actions" /></tr></thead><tbody>{myRequests.map((request, index) => <tr key={request.id} className={index % 2 ? "is-alt" : ""}><td><span className="moCellTitle"><span className="moCellIcon"><Gift size={14} /></span>{request.avantageNom || request.avantageSocialNom || "Avantage social"}</span></td><td>{request.motif || "—"}</td><td>{request.createdAt ? new Date(request.createdAt).toLocaleDateString("fr-FR") : "—"}</td><td><span className={`moStatus moStatus--${String(request.status || "PENDING").toLowerCase()}`}>{statusLabel(request.status)}</span></td><td className="moActions"><button type="button" className="moDetailsBtn" onClick={() => { const item = (avantagesDisponibles || avantages || []).find((av) => av.id === request.avantageSocialId); if (item) openDetail(item); }}><Eye size={12} /> Détails</button></td></tr>)}</tbody></table></div>) : (avantagesAttribues?.length ? <div className="moPickerList">{avantagesAttribues.map((av) => { const automatique = String(av.modeAttribution || av.attributionMode || "").toUpperCase() === "AUTOMATIQUE"; return <div key={av.id} className="moPickerItem"><span className="moCellIcon"><Gift size={15} /></span><span><strong>{av.nom}</strong><small>{av.description || "Avantage social attribué"}</small><em className={`moAttribution ${automatique ? "is-auto" : "is-approved"}`}>{automatique ? "Attribué automatiquement" : "Attribué après validation"}</em></span></div>; })}</div> : <div className="moEmpty"><Gift size={22} /><strong>Aucun avantage attribué</strong></div>)}</div></div>
            {showBenefitPicker && <div className="maModal" onClick={() => setShowBenefitPicker(false)}><div className="maModal__panel" onClick={(e) => e.stopPropagation()}><div className="maModal__header"><div><div className="maModal__title">Nouvelle demande d'avantage</div><div className="maModal__sub">Sélectionnez l'avantage souhaité</div></div><button type="button" className="maIconBtn" onClick={() => setShowBenefitPicker(false)}>×</button></div><div className="maModal__body"><div className="moPickerList">{(avantagesDisponibles || avantages || []).filter((av) => av.actif !== false).map((av) => <button key={av.id} type="button" className="moPickerItem" onClick={() => openDetail(av)}><span className="moCellIcon"><Gift size={15} /></span><span><strong>{av.nom}</strong><small>{av.description || "Avantage social"}</small></span><Plus size={16} /></button>)}</div></div></div></div>}
            {showAssignedBenefits && <div className="maModal" onClick={() => setShowAssignedBenefits(false)}><div className="maModal__panel" onClick={(e) => e.stopPropagation()}><div className="maModal__header"><div><div className="maModal__title">Mes avantages attribués</div><div className="maModal__sub">Avantages affectés automatiquement ou après validation</div></div><button type="button" className="maIconBtn" onClick={() => setShowAssignedBenefits(false)}>×</button></div><div className="maModal__body"><div className="moPickerList">{avantagesAttribues?.length ? avantagesAttribues.map((av) => { const automatique = String(av.modeAttribution || av.attributionMode || "").toUpperCase() === "AUTOMATIQUE"; return <div key={av.id} className="moPickerItem"><span className="moCellIcon"><Gift size={15} /></span><span><strong>{av.nom}</strong><small>{av.description || "Avantage social attribué"}</small><em className={`moAttribution ${automatique ? "is-auto" : "is-approved"}`}>{automatique ? "Attribué automatiquement" : "Attribué après validation"}</em></span></div>; }) : <div className="moEmpty"><Gift size={22} /><strong>Aucun avantage attribué</strong></div>}</div></div></div></div>}

            <div className="maSummary">
                <div className="maSummaryCard">
                    <div className="maSummaryIcon">
                        <Gift size={24} />
                    </div>
                    <div className="maSummaryContent">
                        <div className="maSummaryLabel">Total des avantages</div>
                        <div className="maSummaryValue">{avantages?.length || 0}</div>
                    </div>
                </div>

                {/*<div className="maSummaryCard maSummaryCard--accent">*/}
                {/*    <div className="maSummaryIcon">*/}
                {/*        <DollarSign size={24} />*/}
                {/*    </div>*/}
                {/*    <div className="maSummaryContent">*/}
                {/*        <div className="maSummaryLabel">Montant total mensuel</div>*/}
                {/*        <div className="maSummaryValue">{montantTotal.toFixed(2)} DHS</div>*/}
                {/*    </div>*/}
                {/*</div>*/}

                <div className="maSummaryCard">
                    <div className="maSummaryIcon">
                        <CheckCircle size={24} />
                    </div>
                    <div className="maSummaryContent">
                        <div className="maSummaryLabel">Catégories actives</div>
                        <div className="maSummaryValue">{avantagesParCategorie.length}</div>
                    </div>
                </div>
            </div>

            {/* Liste des avantages par catégorie */}
            <div className="maContent">
                {avantagesTab === "demandes" && <>
                {false && avantagesAttribues?.length > 0 && (
                    <div className="maCategory">
                        <div className="maCategory__header"><div className="maCategory__left"><div><div className="maCategory__name">Mes avantages attribués</div><div className="maMuted">Attribués automatiquement ou après validation</div></div></div></div>
                        <div className="maCategory__content"><div className="maGrid">{avantagesAttribues.map((av) => <div className="maCard" key={av.id}><div className="maCard__header"><span className="maCard__title">{av.nom}</span><span className="maCode">Attribué</span></div>{av.description && <div className="maCard__desc">{av.description}</div>}</div>)}</div></div>
                    </div>
                )}
                {myRequests.length > 0 && (
                    <div className="maCategory">
                        <div className="maCategory__header">
                            <div className="maCategory__left"><div><div className="maCategory__name">Mes demandes d'avantages</div><div className="maMuted">Suivi du circuit de validation</div></div></div>
                        </div>
                        <div className="maCategory__content"><div className="maGrid">
                            {myRequests.map((request) => (
                                <div className="maCard" key={request.id}>
                                    <div className="maCard__header"><span className="maCard__title">{request.avantageNom}</span><span className="maCode">{statusLabel(request.status)}</span></div>
                                    <div className="maCard__desc">{request.motif}</div>
                                </div>
                            ))}
                        </div></div>
                    </div>
                )}
                {avantagesParCategorie.length === 0 && !loading && (
                    <div className="maEmpty">
                        <Gift size={48} />
                        <h3>Aucun avantage disponible</h3>
                        <p>Vous n'avez pas encore d'avantages sociaux configurés.</p>
                    </div>
                )}

                {avantagesParCategorie.map((cat) => {
                    const Icon = getIconComponent(cat.icone);
                    const isExpanded = expandedCategories.has(cat.id);

                    return (
                        <div className="maCategory" key={cat.id}>
                            <div
                                className="maCategory__header"
                                onClick={() => toggleCategory(cat.id)}
                            >
                                <div className="maCategory__left">
                                    <div
                                        className="maCategory__icon"
                                        style={{ backgroundColor: cat.couleur }}
                                    >
                                        <Icon size={20} />
                                    </div>
                                    <div>
                                        <div className="maCategory__name">{cat.nom}</div>
                                        <div className="maMuted">
                                            {cat.avantages.length} avantage{cat.avantages.length > 1 ? "s" : ""}
                                        </div>
                                    </div>
                                </div>

                                <div className="maCategory__right">
                                    {cat.totalMontant > 0 && (
                                        <div className="maCategory__total">
                                            {cat.totalMontant.toFixed(2)} DHS
                                        </div>
                                    )}
                                    <button className="maIconBtn" type="button">
                                        {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                    </button>
                                </div>
                            </div>

                            {isExpanded && (
                                <div className="maCategory__content">
                                    <div className="maGrid">
                                        {cat.avantages.map((av) => (
                                            <div
                                                className="maCard"
                                                key={av.id}
                                                onClick={() => openDetail(av)}
                                            >
                                                <div className="maCard__header">
                                                    <div className="maCard__titleRow">
                                                        <span
                                                            className="maCode"
                                                            style={{ backgroundColor: cat.couleur }}
                                                        >
                                                            {av.code}
                                                        </span>
                                                        <span className="maCard__title">{av.nom}</span>
                                                    </div>
                                                    <button
                                                        className="maInfoBtn"
                                                        type="button"
                                                        title="Plus de détails"
                                                    >
                                                        <Info size={16} />
                                                    </button>
                                                </div>

                                                {av.description && (
                                                    <div className="maCard__desc">{av.description}</div>
                                                )}

                                                {/*<div className="maCard__details">*/}
                                                {/*    {av.montant !== null && av.montant !== undefined && (*/}
                                                {/*        <div className="maDetail">*/}
                                                {/*            <DollarSign size={14} />*/}
                                                {/*            <span className="maDetail__value">*/}
                                                {/*                {av.montant} DHS*/}
                                                {/*            </span>*/}
                                                {/*        </div>*/}
                                                {/*    )}*/}

                                                {/*    <div className="maDetail">*/}
                                                {/*        <Calendar size={14} />*/}
                                                {/*        <span>{getFrequenceLabel(av.frequence)}</span>*/}
                                                {/*    </div>*/}
                                                {/*</div>*/}

                                                {av.regle && (
                                                    <div className="maCard__footer">
                                                        <div className="maRegle">
                                                            <span className="maRegle__label">Règle:</span>
                                                            <span className="maRegle__value">{av.regle.nom}</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
                </>}
            </div>

            {/* Modal de détail */}
            {selectedAvantage && (
                <div className="maModal" onClick={closeDetail}>
                    <div
                        className="maModal__panel"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                    >
                        <div className="maModal__header">
                            <div>
                                <div className="maModal__titleRow">
                                    <span
                                        className="maCode maCode--large"
                                        style={{
                                            backgroundColor:
                                                categories.find((c) => c.id === selectedAvantage.categorieId)?.couleur ||
                                                "#ee645f",
                                        }}
                                    >
                                        {selectedAvantage.code}
                                    </span>
                                    <h2 className="maModal__title">{selectedAvantage.nom}</h2>
                                </div>
                                {selectedAvantage.description && (
                                    <p className="maModal__subtitle">{selectedAvantage.description}</p>
                                )}
                            </div>
                            <button
                                className="maIconBtn"
                                onClick={closeDetail}
                                type="button"
                                aria-label="Fermer"
                            >
                                ×
                            </button>
                        </div>

                        <div className="maModal__body">
                            <div className="maDetailGrid">
                                {selectedAvantage.montant !== null && selectedAvantage.montant !== undefined && (
                                    <div className="maDetailBox">
                                        <div className="maDetailBox__label">Montant</div>
                                        <div className="maDetailBox__value maDetailBox__value--accent">
                                            {selectedAvantage.montant} DHS
                                        </div>
                                    </div>
                                )}

                                <div className="maDetailBox">
                                    <div className="maDetailBox__label">Fréquence</div>
                                    <div className="maDetailBox__value">
                                        {getFrequenceLabel(selectedAvantage.frequence)}
                                    </div>
                                </div>

                                {selectedAvantage.regle && (
                                    <>
                                        <div className="maDetailBox maDetailBox--wide">
                                            <div className="maDetailBox__label">Règle d'attribution</div>
                                            <div className="maDetailBox__value">{selectedAvantage.regle.nom}</div>
                                        </div>

                                        {selectedAvantage.regle.condition && (
                                            <div className="maDetailBox maDetailBox--wide">
                                                <div className="maDetailBox__label">Condition</div>
                                                <div className="maDetailBox__text">
                                                    {selectedAvantage.regle.condition}
                                                </div>
                                            </div>
                                        )}

                                        {selectedAvantage.regle.valeur && (
                                            <div className="maDetailBox maDetailBox--wide">
                                                <div className="maDetailBox__label">Valeur</div>
                                                <div className="maDetailBox__text">
                                                    {selectedAvantage.regle.valeur}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            <div className="maDetailGrid" style={{ marginTop: 18 }}>
                                <div className="maDetailBox maDetailBox--wide">
                                    <div className="maDetailBox__label">Demander cet avantage</div>
                                    <input className="maRequestInput" value={requestMotif} onChange={(e) => setRequestMotif(e.target.value)} placeholder="Motif de la demande (facultatif)" />
                                    <textarea className="maRequestInput" value={requestComment} onChange={(e) => setRequestComment(e.target.value)} placeholder="Commentaire complémentaire (facultatif)" rows="3" />
                                    {requestError && <div className="maRequestError">{requestError}</div>}
                                </div>
                            </div>
                        </div>

                        <div className="maModal__footer">
                            <button className="maBtn maBtn--primary" onClick={closeDetail} type="button">
                                Fermer
                            </button>
                            <button className="maBtn maBtn--primary" onClick={submitRequest} type="button">
                                Envoyer la demande
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MesAvantages;
